import React, { useState, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import { useI18n } from '@locales/client';

import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box';
import { validateSchema } from '@/shared/libs/validate';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';
import { CardNumberField } from '@/shared/ui/form/CardNumberField';
import { ExpireInput } from '@/shared/ui/form/expire-input';
import { CvvInput } from '@/shared/ui/form/cvv-input';
import { FormField } from '@/shared/ui/form/form-field';

import { populateFieldsWithPreviousValues } from '@/features/wallet-managment/model/utils';
import {
  holderCardSchema,
  cardSchema,
} from '@/features/wallet-managment/model/schemas';

import { AdressesDetailFiat } from '../../../address-detail';
import { WithdrawButton } from '../../styles';

import { InputField, FormItem, FormGroup, BoxCvv, BoxExpire } from './styles';

type CardFormProps = {
  handleSubmit: (data: any) => void;
  settings?: {
    fields?: string[];
  };
  valuesForm?: Record<string, any>;
  withHolder?: boolean;
  isLocked?: boolean;
};

const CardForm = ({
  handleSubmit,
  settings,
  valuesForm,
  withHolder = false,
  isLocked = false,
}: CardFormProps) => {
  const t = useI18n();
  const expireMonthRef = useRef<HTMLInputElement>(null);
  const expireYearRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);

  const [form] = useState(() => ({
    card: '',
    expireMonth: '',
    expireYear: '',
    cvv: '',
    ...(withHolder ? { holder: '' } : {}),
    save: true,
  }));

  const validate = (values: any) => {
    const schema = withHolder ? holderCardSchema(t) : cardSchema(t);
    const getSchema = validateSchema(schema);
    return getSchema(values);
  };

  const onSubmit = (val: any) => {
    let data = { ...val };

    if (settings?.fields?.includes('browserInfo')) {
      data = {
        ...data,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        timeZone: new Date().getTimezoneOffset(),
      };
    }

    if (valuesForm) {
      data = { ...valuesForm, ...data };
    }

    handleSubmit(data);
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={form}
      validate={validate}
      render={(props) => {
        const isDisabled =
          props.submitting || props.form.getState().invalid || isLocked;

        return (
          <FormGroup onSubmit={props.handleSubmit}>
            <Box
              $display="flex"
              $flexDirection="column"
              $gap="12px"
              $marginBottom="64px"
            >
              <FormItem>
                <AdressesDetailFiat
                  keyAddres={'card'}
                  inputValue={props?.values}
                  onEdit={(address) =>
                    populateFieldsWithPreviousValues(props, address)
                  }
                />
                <BodyTextRegularSecondary color="#AFAFAF">
                  {t('wallet.fiat.card.bankCard')}
                </BodyTextRegularSecondary>
                <InputField $roundTop={true} $roundBottom={true}>
                  <Field
                    name={'card'}
                    component={CardNumberField}
                    bgColor="#4B444C"
                    type={'text'}
                    placeholder={t('wallet.fiat.card.title')}
                    placeholderColor="#AFAFAF"
                    label={
                      <BodyTextBoldSecondary>
                        {t('wallet.fiat.card.number')}
                      </BodyTextBoldSecondary>
                    }
                  />
                  <Box
                    $display="flex"
                    $justifyContent="space-between"
                    $width="100%"
                  >
                    <BoxExpire>
                      <BodyTextBoldSecondary>
                        {t('wallet.fiat.input_fields.expire.label')}
                      </BodyTextBoldSecondary>
                      <Box $display="flex" $marginTop="5px">
                        <Field
                          name={'expireMonth'}
                          component={ExpireInput}
                          bgColor="#4B444C"
                          small={true}
                          ref={expireMonthRef}
                          refNex={expireYearRef}
                          type={'text'}
                          placeholder={t(
                            'wallet.fiat.input_fields.expire.placeholder_month'
                          )}
                          placeholderColor="#AFAFAF"
                        />
                        <Box $margin="16px 8px">/</Box>
                        <Field
                          name={'expireYear'}
                          component={ExpireInput}
                          ref={expireYearRef}
                          refNex={cvvRef}
                          bgColor="#4B444C"
                          small={true}
                          type={'text'}
                          placeholder={t(
                            'wallet.fiat.input_fields.expire.placeholder_year'
                          )}
                          placeholderColor="#AFAFAF"
                        />
                      </Box>
                    </BoxExpire>
                    <BoxCvv>
                      <BodyTextBoldSecondary>
                        {t('wallet.fiat.input_fields.cvv.label')}
                      </BodyTextBoldSecondary>
                      <Box $marginTop="5px">
                        <Field
                          name={'cvv'}
                          component={CvvInput}
                          small={true}
                          bgColor="#4B444C"
                          placeholder={t(
                            'wallet.fiat.input_fields.cvv.placeholder'
                          )}
                          placeholderColor="#AFAFAF"
                          type={'text'}
                          ref={cvvRef}
                        />
                      </Box>
                    </BoxCvv>
                  </Box>
                </InputField>
              </FormItem>

              {withHolder && (
                <FormItem>
                  <InputField $roundTop={true} $roundBottom={true}>
                    <Field
                      name={'holder'}
                      component={FormField}
                      bgColor="#4B444C"
                      type={'text'}
                      placeholder={t(
                        'wallet.fiat.input_fields.fname.placeholder'
                      )}
                      placeholderColor="#AFAFAF"
                      label={
                        <BodyTextBoldSecondary>
                          {t('wallet.fiat.card.card_holder')}
                        </BodyTextBoldSecondary>
                      }
                    />
                  </InputField>
                </FormItem>
              )}

              <FormItem>
                <Field
                  name="save"
                  component={CheckboxField}
                  type="checkbox"
                  label={
                    <BodyTextRegularSecondary>
                      {t('wallet.fiat.input_fields.sections.save')}
                    </BodyTextRegularSecondary>
                  }
                />
              </FormItem>
            </Box>

            <Box
              $position="absolute"
              $bottom="18px"
              $borderRadius="8px"
              $opacity="1"
              $width="calc(100% - 36px)"
              $zIndex="100"
              $backgroundColor="#3D3C41"
            >
              <WithdrawButton
                type="submit"
                variant={isDisabled ? 'gray' : 'orangeGradient'}
                disabled={isDisabled}
                $isLoading={isLocked}
              >
                {t('wallet.fiat.pay')}
              </WithdrawButton>
            </Box>
          </FormGroup>
        );
      }}
    />
  );
};

export { CardForm };
